/**
 * ToonIntro - 깨봉툰 인트로 뷰어 (qn_i)
 *
 * 페이지별 이미지 + 텍스트 + 오디오.
 * Prev/Next 네비게이션, 마지막 페이지에서 완료 버튼.
 */
import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Lottie from 'lottie-react'
import { ChevronLeft, Volume2 } from 'lucide-react'
import { useToonPlayer } from './useToonPlayer'
import buttonAnimation from '../../assets/toon/lottie/toon_button_reaction.json'
import type { ToonIntroProps } from './types'

export function ToonIntro({ cards, onComplete, autoPlayAudio = false }: ToonIntroProps) {
  const {
    currentPage,
    direction,
    isFirst,
    isLast,
    goNext,
    goPrev,
    playAudio,
  } = useToonPlayer({ totalPages: cards.length, autoPlayAudio })

  const card = cards[currentPage]

  // 페이지 전환 시 오디오 자동 재생
  useEffect(() => {
    if (card?.audioUrl) {
      playAudio(card.audioUrl)
    }
  }, [currentPage, card?.audioUrl, playAudio])

  const slideVariants = {
    enter: (dir: string) => ({
      x: dir === 'right' ? 300 : -300,
      opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (dir: string) => ({
      x: dir === 'right' ? -300 : 300,
      opacity: 0,
    }),
  }

  return (
    <div className="relative w-full max-w-[720px] md:max-w-[800px] mx-auto">
      {/* 메인 컨테이너 */}
        <div className="relative w-full min-h-[480px] overflow-hidden bg-white rounded-3xl shadow-xl">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentPage}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="flex flex-col items-center w-full px-8 py-8 pb-28"
            >
              {/* 텍스트 + 오디오 */}
              {card?.text && (
                <div className="relative w-full py-4 pl-16 pr-4 font-medium text-center text-gray-700 bg-gray-100 rounded-2xl text-base md:text-lg">
                  {card.audioUrl && (
                    <button
                      className="absolute left-4 top-3 w-10 h-10 flex items-center justify-center rounded-xl bg-orange-400 hover:bg-orange-500 transition-colors"
                      onClick={() => playAudio(card.audioUrl)}
                    >
                      <Volume2 className="w-5 h-5 text-white" />
                    </button>
                  )}
                  {card.text}
                </div>
              )}

              {/* 이미지 */}
              {card?.imageUrl && (
                <div
                  className="w-full md:w-[640px] h-[320px] bg-no-repeat bg-center mt-6 bg-contain"
                  style={{ backgroundImage: `url(${card.imageUrl})` }}
                />
              )}
            </motion.div>
          </AnimatePresence>

          {/* 완료 버튼 (마지막 페이지) */}
          {isLast && (
            <motion.button
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.3, ease: 'easeOut' }}
              className="absolute bottom-8 right-8 px-6 py-2 min-w-[120px] font-bold text-white bg-orange-400 rounded-2xl
                [box-shadow:0px_4px_0px_#E88A1C,0px_8px_0px_rgba(0,0,0,0.2)]
                hover:bg-orange-500 active:translate-y-1 active:[box-shadow:0px_2px_0px_#E88A1C] transition-all"
              onClick={onComplete}
            >
              완료
            </motion.button>
          )}
        </div>

        {/* Prev 버튼 */}
        {!isFirst && (
          <button
            onClick={goPrev}
            className="absolute top-1/2 left-2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center
              [box-shadow:0px_2px_0px_rgba(0,0,0,0.1)] hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-gray-500" />
          </button>
        )}

        {/* Next 버튼 (Lottie 애니메이션) */}
        {!isLast && (
          <button
            onClick={goNext}
            className="absolute top-[calc(50%-20px)] -right-2 z-10"
          >
            <Lottie
              animationData={buttonAnimation}
              loop={false}
              autoplay
              style={{ width: 80 }}
            />
          </button>
        )}

        {/* 페이지 인디케이터 */}
        <div className="flex justify-center gap-1.5 mt-4">
          {cards.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentPage ? 'bg-orange-400' : 'bg-lc-gray-300'
              }`}
            />
          ))}
        </div>
    </div>
  )
}
